import { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import Paper from "@mui/material/Paper";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import { getAuth } from "firebase/auth";
import {
  collection,
  doc,
  getFirestore,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
  type Timestamp,
} from "firebase/firestore";
import { parseActiveProject } from "../lib/activeProjectParser";
import { JarvisTheme } from "../../theme/jarvisTheme";

type RangeFilter = "today" | "week" | "month" | "all";

type JarvisEntryDoc = {
  id: string;
  userId: string;
  type: string;
  date: string;
  data: Record<string, unknown>;
  summary: string;
  tags: string[];
  createdAt: Timestamp | null;
};

function pad2(n: number): string {
  return n.toString().padStart(2, "0");
}

function formatYmd(year: number, month: number, day: number): string {
  return `${year}-${pad2(month)}-${pad2(day)}`;
}

function localYmd(date: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  const year = Number(parts.find((p) => p.type === "year")?.value);
  const month = Number(parts.find((p) => p.type === "month")?.value);
  const day = Number(parts.find((p) => p.type === "day")?.value);
  return formatYmd(year, month, day);
}

function addDaysYmd(ymd: string, delta: number): string {
  const [y, m, d] = ymd.split("-").map(Number);
  const next = new Date(y, m - 1, d + delta);
  return formatYmd(next.getFullYear(), next.getMonth() + 1, next.getDate());
}

function dateRangeForFilter(filter: RangeFilter): {
  startDate: string;
  endDate: string;
} {
  const today = localYmd();
  switch (filter) {
    case "today":
      return { startDate: today, endDate: today };
    case "week":
      return { startDate: addDaysYmd(today, -6), endDate: today };
    case "month":
      return { startDate: addDaysYmd(today, -29), endDate: today };
    case "all":
      return { startDate: addDaysYmd(today, -364), endDate: today };
  }
}

function formatDataField(key: string, value: unknown): string {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return JSON.stringify(value);
}

const RANGE_LABELS: Record<RangeFilter, string> = {
  today: "Today",
  week: "This week",
  month: "This month",
  all: "All (1y)",
};

export function LogsTab() {
  const [entries, setEntries] = useState<JarvisEntryDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [range, setRange] = useState<RangeFilter>("week");
  const [activeProject, setActiveProject] = useState("");

  const uid = getAuth().currentUser?.uid ?? null;
  const projectConfig = useMemo(
    () => parseActiveProject(activeProject),
    [activeProject],
  );
  const entryType = projectConfig?.entryType?.trim() ?? "";

  useEffect(() => {
    if (!uid) {
      setActiveProject("");
      return;
    }

    const ref = doc(
      getFirestore(),
      "jarvis_memory",
      uid,
      "active_project",
      "data",
    );

    const unsub = onSnapshot(
      ref,
      (snap) => {
        const data = snap.data();
        setActiveProject(
          typeof data?.content === "string" ? data.content.trim() : "",
        );
      },
      () => setActiveProject(""),
    );

    return () => unsub();
  }, [uid]);

  useEffect(() => {
    if (!uid) {
      setLoading(false);
      setError("Sign in required.");
      return;
    }

    setLoading(true);
    setError(null);

    const col = collection(getFirestore(), "jarvis_entries", uid, "entries");
    const { startDate, endDate } = dateRangeForFilter(range);

    // orderBy must match firestore.indexes.json (date ASC, createdAt DESC)
    const q = entryType
      ? query(
          col,
          where("type", "==", entryType),
          where("date", ">=", startDate),
          where("date", "<=", endDate),
          orderBy("date", "asc"),
          orderBy("createdAt", "desc"),
          limit(50),
        )
      : query(
          col,
          where("date", ">=", startDate),
          where("date", "<=", endDate),
          orderBy("date", "asc"),
          orderBy("createdAt", "desc"),
          limit(50),
        );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows: JarvisEntryDoc[] = snap.docs.map((d) => {
          const data = d.data();
          const rawData =
            data.data && typeof data.data === "object"
              ? (data.data as Record<string, unknown>)
              : {};
          const tags = Array.isArray(data.tags)
            ? data.tags.filter((t): t is string => typeof t === "string")
            : [];

          return {
            id: d.id,
            userId: typeof data.userId === "string" ? data.userId : uid,
            type: typeof data.type === "string" ? data.type : "",
            date: typeof data.date === "string" ? data.date : "",
            data: rawData,
            summary: typeof data.summary === "string" ? data.summary : "",
            tags,
            createdAt: data.createdAt ?? null,
          };
        });
        setEntries(rows);
        setLoading(false);
        setError(null);
      },
      (err) => {
        setError(err.message || "Failed to load entries.");
        setLoading(false);
      },
    );

    return () => unsub();
  }, [uid, range, entryType]);

  const focusLabel = projectConfig?.projectTitle ?? null;

  const sortedEntries = useMemo(() => {
    return [...entries].sort((a, b) => {
      if (a.date !== b.date) return b.date.localeCompare(a.date);
      const aMs = a.createdAt?.toMillis?.() ?? 0;
      const bMs = b.createdAt?.toMillis?.() ?? 0;
      return bMs - aMs;
    });
  }, [entries]);

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography
          sx={{
            fontFamily: JarvisTheme.fonts.ui,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            fontSize: "0.8rem",
            color: JarvisTheme.colors.muted,
            mb: 0.5,
            opacity: 0.85,
          }}
        >
          Logs
        </Typography>
        <Typography
          sx={{
            opacity: 0.72,
            fontSize: "0.85rem",
            color: JarvisTheme.colors.muted,
          }}
        >
          Entries recorded via Jarvis chat
          {entryType ? ` (${entryType})` : ""}.
          {!entryType &&
            " Switch to work mode in Chat to filter work logs."}
        </Typography>
      </Box>

      {focusLabel && (
        <Chip
          label={`Focus: ${focusLabel}`}
          size="small"
          sx={{
            mb: 2,
            height: 24,
            fontSize: "0.7rem",
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            fontFamily: JarvisTheme.fonts.mono,
            bgcolor: JarvisTheme.colors.accentSoft,
            color: JarvisTheme.colors.accent,
            border: `1px solid ${JarvisTheme.colors.accentBorder}`,
          }}
        />
      )}

      <Box
        sx={{
          display: "flex",
          gap: 1,
          mb: 2,
          flexWrap: "wrap",
        }}
      >
        {(Object.keys(RANGE_LABELS) as RangeFilter[]).map((key) => (
          <Button
            key={key}
            variant={range === key ? "contained" : "outlined"}
            size="small"
            onClick={() => setRange(key)}
            sx={{
              fontFamily: JarvisTheme.fonts.ui,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              borderColor: JarvisTheme.colors.panelBorder,
              color:
                range === key ? JarvisTheme.colors.bg0 : JarvisTheme.colors.muted,
              bgcolor:
                range === key ? JarvisTheme.colors.accent : "transparent",
              "&:hover": {
                borderColor: JarvisTheme.colors.accent,
                bgcolor:
                  range === key
                    ? "rgba(88, 230, 255, 0.85)"
                    : JarvisTheme.colors.panel,
              },
            }}
          >
            {RANGE_LABELS[key]}
          </Button>
        ))}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress
            size={22}
            sx={{ color: JarvisTheme.colors.accent }}
          />
        </Box>
      ) : sortedEntries.length === 0 ? (
        <Alert severity="info">
          No entries for this range. Log something in Chat — e.g. what you worked
          on today.
        </Alert>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {sortedEntries.map((entry) => {
            const dataKeys = Object.keys(entry.data).filter(
              (k) => entry.data[k] != null && String(entry.data[k]).trim() !== "",
            );

            return (
              <Paper
                key={entry.id}
                elevation={0}
                sx={{
                  p: 1.5,
                  borderRadius: 1,
                  border: `1px solid ${JarvisTheme.colors.panelBorder}`,
                  bgcolor: JarvisTheme.colors.panelSoft,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: 1,
                    mb: 0.75,
                    flexWrap: "wrap",
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: JarvisTheme.fonts.ui,
                      fontSize: "0.95rem",
                      color: JarvisTheme.colors.text,
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    {entry.summary || "(no summary)"}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: JarvisTheme.fonts.mono,
                      fontSize: "0.7rem",
                      color: JarvisTheme.colors.muted,
                      letterSpacing: "0.06em",
                      flexShrink: 0,
                    }}
                  >
                    {entry.date}
                    {!entryType && entry.type ? ` · ${entry.type}` : ""}
                  </Typography>
                </Box>

                {dataKeys.length > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.5,
                      mb: entry.tags.length > 0 ? 1 : 0,
                    }}
                  >
                    {dataKeys.map((key) => (
                      <Typography
                        key={key}
                        sx={{
                          fontFamily: JarvisTheme.fonts.ui,
                          fontSize: "0.82rem",
                          color: JarvisTheme.colors.muted,
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            color: JarvisTheme.colors.text,
                            opacity: 0.85,
                            textTransform: "capitalize",
                          }}
                        >
                          {key}:
                        </Box>{" "}
                        {formatDataField(key, entry.data[key])}
                      </Typography>
                    ))}
                  </Box>
                )}

                {entry.tags.length > 0 && (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {entry.tags.map((tag) => (
                      <Chip
                        key={tag}
                        label={tag}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: "0.65rem",
                          fontFamily: JarvisTheme.fonts.mono,
                          bgcolor: JarvisTheme.colors.panel,
                          color: JarvisTheme.colors.muted,
                          border: `1px solid ${JarvisTheme.colors.panelBorder}`,
                        }}
                      />
                    ))}
                  </Box>
                )}
              </Paper>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
